/**
 * Common translations: common, help, keyboard, password, notifications, breadcrumbs, quickActions, globalSearch, ticket, validation, pageTitles, errors
 */

export const common = {
  notFoundPage: {
    title: 'Page not found',
    description: 'This page does not exist, or its address has changed.',
    backHome: 'Back to the homepage'
  },
  // BCP-47 locale tag for toLocaleDateString and similar APIs.
  locale: 'en-GB',
  save: 'Save',
  cancel: 'Cancel',
  delete: 'Delete',
  confirm: 'Confirm',
  done: 'Done',
  edit: 'Edit',
  add: 'Add',
  back: 'Back',
  next: 'Next',
  loading: 'Loading...',
  error: 'Error',
  success: 'Success',
  important: 'Important',
  close: 'Close',
  search: 'Search',
  filter: 'Filter',
  reset: 'Reset',
  retry: 'Retry',
  freeForever: 'Free forever',
  upgradeAnytime: 'Upgrade to a paid tier anytime.',
  yes: 'Yes',
  no: 'No',
  or: 'or',
  all: 'All',
  none: 'None',
  optional: 'Optional',
  required: 'Required',
  active: 'Active',
  creating: 'Creating...',
  settings: 'Settings',
  copied: 'Copied',
  refresh: 'Refresh',
  justNow: 'just now',
  minutesAgo: '{n}m ago',
  hoursAgo: '{n}h ago',
  daysAgo: '{n}d ago',
  viewAll: 'View all',
  termPrice: {
    period: {
      month: 'month',
      quarter: 'quarter',
      halfYear: 'half-year',
      year: 'year'
    },
    monthlyEquivalent: 'equals {price} / month'
  }
} as const

export const help = {
  title: 'Help & Contact',
  weHelpYou: 'We are happy to help.'
} as const

export const keyboard = {
  title: 'Keyboard Shortcuts',
  categories: {
    search: 'Search',
    navigation: 'Navigation',
    general: 'General',
    dev: 'Development'
  },
  noShortcuts: 'No shortcuts in this category',
  ctrlKNote: 'Ctrl+K also opens the search'
} as const

export const password = {
  strength: {
    veryWeak: 'very weak',
    weak: 'weak',
    fair: 'fair',
    good: 'good',
    strong: 'strong',
    label: '{strength} password'
  },
  requirements: {
    title: 'Password Requirements',
    minLength: 'At least 8 characters',
    uppercase: 'Uppercase letter (A-Z)',
    lowercase: 'Lowercase letter (a-z)',
    number: 'Number (0-9)'
  }
} as const

export const notifications = {
  title: 'Notifications',
  ariaLabel: 'Notifications',
  markAllRead: 'Mark all as read',
  empty: 'No notifications',
  categories: {
    renewal: 'Renewal',
    security: 'Security',
    billing: 'Billing',
    system: 'System',
    verification: 'Verification'
  },
  saved: 'Saved successfully',
  updated: 'Updated successfully',
  deleted: 'Deleted successfully',
  created: 'Created successfully',
  addedToCart: 'Added to cart',
  copiedToClipboard: 'Copied to clipboard'
} as const

export const breadcrumbs = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  email: 'Email',
  domains: 'Domains',
  packet: 'Packages',
  invoice: 'Invoices',
  account: 'Account',
  product: 'Products',
  user: 'Users',
  details: 'Details',
  security: 'Security'
} as const

export const quickActions = {
  title: 'Quick Actions',
  ariaLabel: 'Quick Actions',
  newTicket: 'New Ticket',
  orderProduct: 'Order Product',
  domains: 'Domains',
  newProduct: 'New Product',
  adminBadge: 'Admin'
} as const

export const globalSearch = {
  triggerText: 'Search...',
  openSearch: 'Open search',
  openSearchShortcut: 'Open search (Ctrl+K)',
  placeholder: 'Search for pages, products, domains...',
  clearInput: 'Clear input',
  closeSearch: 'Close search',
  noResults: 'No results for "{query}"',
  recentSearches: 'Recent Searches',
  clearRecent: 'Clear',
  hints: {
    navigate: 'navigate',
    select: 'select',
    close: 'close'
  },
  groups: {
    domain: 'Domain',
    navigation: 'Navigation',
    product: 'Products'
  },
  domainResult: {
    title: 'Check domain "{query}"',
    subtitle: 'Check availability'
  },
  nav: {
    home: 'Home',
    kubernetes: 'Kubernetes',
    kubernetesSub: 'Container orchestration',
    vserver: 'VServer',
    vserverSub: 'Virtual private servers',
    webhosting: 'Web Hosting',
    webhostingSub: 'Hosting packages',
    domains: 'Domains',
    domainsSub: 'Search & register a domain',
    products: 'All Products',
    productsSub: 'Product overview',
    cart: 'Shopping cart',
    login: 'Sign in',
    register: 'Register',
    dashboard: 'Dashboard',
    dashboardSub: 'My area',
    impress: 'Legal Notice',
    privacy: 'Privacy Policy',
    agb: 'Terms & Conditions',
    agbSub: 'General Terms and Conditions'
  }
} as const

export const ticket = {
  title: 'Support Ticket',
  subtitle: 'How can we help you?',
  responseTime: 'Our support team typically responds within 24 hours.',
  loadError: 'The ticket form could not be loaded. Please try again later or email us directly at support{\'@\'}itsh.dev.'
} as const

export const a11y = {
  skipToContent: 'Skip to content',
  mainNavigation: 'Main navigation',
  bottomNavigation: 'Bottom navigation',
  shoppingCart: 'Shopping cart',
  goToCart: 'Go to cart',
  logoToHome: 'ITSH - To homepage'
} as const

export const validation = {
  required: 'Required',
  email: 'Invalid email address',
  emailFormat: 'Invalid email format',
  number: 'Only numbers allowed',
  minLength: 'At least {min} characters required',
  maxLength: 'Maximum {max} characters allowed',
  phone: 'Only digits allowed (no spaces)',
  postcode: 'Postcode must have 5 digits',
  alphanumeric: 'Only letters, numbers and punctuation allowed',
  noHtml: 'HTML tags are not allowed',
  invalidInput: 'Invalid input',
  invalidPostcodeCountry: "The postal code doesn't match the format of the selected country. Please check your input.",
  postcodeStateMismatch: "The postal code isn't located in the selected state. Please check your input.",
  ipv4: 'Invalid IPv4 address',
  ipv6: 'Invalid IPv6 address',
  hostname: 'Invalid hostname',
  port: 'Must be between 0 and 65535',
  numeric: 'Must be a number',
  emailMissingAt: "Email must contain an {'@'}",
  emailMissingDomain: "Please enter a domain after {'@'} (e.g. example.com)",
  emailInvalidDomain: 'Domain must contain a dot (e.g. example.com)',
  passwordTooShort: 'At least {min} characters required (currently: {current})',
  passwordNeedsUppercase: 'At least one uppercase letter (A-Z) required',
  passwordNeedsLowercase: 'At least one lowercase letter (a-z) required',
  passwordNeedsNumber: 'At least one number (0-9) required',
  phoneNoLetters: 'Phone number must not contain letters',
  phoneNoSpaces: 'Please enter phone number without spaces',
  phoneTooShort: 'Phone number is too short (minimum {min} digits)',
  phoneTooLong: 'Phone number is too long (maximum {max} digits)',
  postcodeTooShort: 'Postcode must have 5 digits (currently: {current})',
  postcodeNoLetters: 'Postcode must only contain digits',
  nameNoNumbers: 'Name must not contain numbers',
  nameInvalidChars: 'Name contains invalid characters'
} as const

export const pageTitles = {
  // Bare page names. The 'ITSH | ' prefix is composed in the router's
  // afterEach hook: vue-i18n reads `|` as the plural delimiter, so storing
  // the full title here truncates it to 'ITSH' at render time.
  products: 'Products',
  kubernetes: 'Managed Kubernetes',
  objectStorage: 'Object Storage',
  webhosting: 'Web Hosting',
  domainSearch: 'Register Domain',
  tldCatalog: 'TLD Overview',
  impress: 'Legal Notice',
  privacy: 'Privacy Policy',
  agb: 'Terms & Conditions',
  sla: 'SLA',
  cancellation: 'Terminate contracts',
  withdrawal: 'Withdrawal instructions',
  nis2: 'NIS2',
  pricingCalculator: 'Pricing Calculator',
  vserverCompare: 'VServer comparison'
} as const

export const errors = {
  generic: 'An error occurred. Please try again later.',
  serviceUnavailable: 'The service is currently unavailable. Please try again later.',
  gatewayError: 'The server is temporarily unreachable. Please try again later.',
  networkError: 'Network error. Please check your internet connection.',
  unauthorized: 'Unauthorised. Please sign in again.',
  forbidden: 'Access denied.',
  notFound: 'The requested resource was not found.',
  conflict: 'Conflict. The resource already exists.',
  tooManyRequests: 'Too many requests. Please wait a moment.',
  saveFailed: 'Failed to save',
  loadFailed: 'Failed to load',
  deleteFailed: 'Failed to delete'
} as const
